import { useEffect, useMemo, useRef, useState } from "react"
import { useRouter } from "next/router"
import { SIDE_PANEL_NAV_EVENT } from "@/core/utils/navigationEvents"

const useNavigationHistory = () => {
  const [history, setHistory] = useState<string[]>([])
  const [isSidePanelNavPending, setIsSidePanelNavPending] =
    useState<boolean>(false)
  const router = useRouter()
  const lastPathRef = useRef<string>("")
  const isInternalBackNavRef = useRef(false)
  const pendingPopRef = useRef<{
    fromUrl: string
    expectedTarget?: string
  } | null>(null)
  const pendingSidePanelClearRef = useRef(false)

  useEffect(() => {
    if (!router.isReady) return
    lastPathRef.current = router.asPath

    const getCurrentUrl = (): string => {
      if (typeof window === "undefined") return router.asPath
      return (
        window.location.pathname + window.location.search + window.location.hash
      )
    }

    const pushCurrentIfNeeded = (nextUrl: string) => {
      if (isInternalBackNavRef.current) return

      const current = lastPathRef.current || getCurrentUrl()
      if (!current || current === nextUrl) return

      const currentPath = current.split("?")[0]?.split("#")[0] ?? ""
      const nextPath = nextUrl.split("?")[0]?.split("#")[0] ?? ""
      if (currentPath === nextPath) return

      setHistory((prev) => {
        const last = prev[prev.length - 1]
        const next = last === current ? prev : [...prev, current]
        return next.slice(-50)
      })
    }

    const handleRouteChangeStart = (nextUrl: string) => {
      pushCurrentIfNeeded(nextUrl)
    }

    const handleRouteChangeComplete = (url: string) => {
      if (
        pendingPopRef.current &&
        url !== pendingPopRef.current.fromUrl &&
        (!pendingPopRef.current.expectedTarget ||
          url === pendingPopRef.current.expectedTarget)
      ) {
        pendingPopRef.current = null
        setHistory((prev) => prev.slice(0, -1))
      }
      lastPathRef.current = url
      isInternalBackNavRef.current = false

      if (pendingSidePanelClearRef.current) {
        pendingSidePanelClearRef.current = false
        setHistory([])
      }
      setIsSidePanelNavPending(false)
    }

    const handleRouteChangeError = () => {
      pendingSidePanelClearRef.current = false
      setIsSidePanelNavPending(false)
    }

    const handleBrowserUrlChange = () => {
      const nextUrl = getCurrentUrl()
      if (pendingPopRef.current && nextUrl !== pendingPopRef.current.fromUrl) {
        // Popstate/back succeeded
        pendingPopRef.current = null
        setHistory((prev) => prev.slice(0, -1))
      }
      pushCurrentIfNeeded(nextUrl)
      lastPathRef.current = nextUrl
      isInternalBackNavRef.current = false
    }

    const originalPushState = window.history.pushState
    const originalReplaceState = window.history.replaceState
    window.history.pushState = function (...args) {
      originalPushState.apply(this, args as any)
      handleBrowserUrlChange()
    }
    window.history.replaceState = function (...args) {
      originalReplaceState.apply(this, args as any)
      handleBrowserUrlChange()
    }

    router.events.on("routeChangeStart", handleRouteChangeStart)
    router.events.on("routeChangeComplete", handleRouteChangeComplete)
    router.events.on("routeChangeError", handleRouteChangeError)
    window.addEventListener("popstate", handleBrowserUrlChange)
    const handleSidePanelNav = () => {
      // Clear only after navigation succeeds (routeChangeComplete).
      pendingSidePanelClearRef.current = true
      setIsSidePanelNavPending(true)
    }
    window.addEventListener(SIDE_PANEL_NAV_EVENT, handleSidePanelNav)
    return () => {
      router.events.off("routeChangeStart", handleRouteChangeStart)
      router.events.off("routeChangeComplete", handleRouteChangeComplete)
      router.events.off("routeChangeError", handleRouteChangeError)
      window.removeEventListener("popstate", handleBrowserUrlChange)
      window.removeEventListener(SIDE_PANEL_NAV_EVENT, handleSidePanelNav)

      window.history.pushState = originalPushState
      window.history.replaceState = originalReplaceState
    }
  }, [router.isReady, router.asPath, router.events])

  useEffect(() => {
    if (history.length === 1 && history[0] === "/") {
      setHistory([])
    }
  }, [history])

  const goBack = () => {
    const fromUrl =
      typeof window === "undefined"
        ? router.asPath
        : window.location.pathname +
          window.location.search +
          window.location.hash

    const target = history[history.length - 1]
    pendingPopRef.current = { fromUrl, expectedTarget: target }

    if (target) {
      isInternalBackNavRef.current = true
      void router.push(target)
    } else {
      router.back()
    }
  }

  return { history, isSidePanelNavPending, goBack }
}

export default useNavigationHistory
